import type { UsageEvent } from '@dcs/contracts';
import type { KeyValueStorage } from '../ports/storage.ts';

/**
 * Anonymous usage tracker (F7 D-2, A-2). Sends `UsageEvent`s to `POST /api/track` in batches.
 *
 * Privacy by construction:
 * - the only identifier is `anonId`, a random UUID v4 kept on the device; no user id, e-mail, IP, URL
 *   or free text ever enters an event: `track` copies the three known fields and drops the rest;
 * - nothing is sent (and no id is created) when the browser says Do Not Track or Global Privacy
 *   Control (`enabled()`), or while offline: analytics only, no queue, no retry.
 *
 * Quota safety (F7 D-5, T-2.8): every request costs the sink `2g + 8` D1 row writes (docs/runbook.md,
 * "Endpoint ghi công khai"), so the tracker sends few, large batches:
 * - a batch leaves when it holds `FLUSH_SIZE` (20, the API maximum) events, `FLUSH_MAX_WAIT_MS` (5 min)
 *   after its FIRST event (later events do not postpone it, so a long session still reports about every
 *   5 minutes), or at once when the page is hidden / unloaded (`web.ts`: `pagehide`, `visibilitychange`
 *   to hidden, via `sendBeacon`, `fetch` keepalive as fallback). 20 events stay under 1.5 KB, the 4 KB
 *   body cap of the API (a test keeps this true);
 * - batches per device: the typical visit (about 15 events, one or two tabs, under 10 minutes) sends
 *   1-2 batches: the max-wait or hide flush, plus at most one more when the tab is left or the 5 minutes
 *   run out again. With 1-1.5 visits a day that is <= ~2.5 batches per device per day, the figure the
 *   daily write cap of the sink (28,000 rows, ~2,500 batches) is sized for at ~1k DAU;
 * - a local cap of `MAX_EVENTS_PER_MINUTE` (below the API's 60/min/anonId) after which events are dropped.
 *
 * Pure: clock, randomness, timers and transport are injected (`createWebTracker` binds the browser).
 */

export const ANON_ID_KEY = 'dcs:anon-id';
/** Flush when this many events are buffered: the API maximum per request (`TrackRequestSchema`). */
export const FLUSH_SIZE = 20;
/** Flush this long after the FIRST buffered event; later events do not reset it. */
export const FLUSH_MAX_WAIT_MS = 5 * 60_000;
/** Local cap per rolling 60 s window; the API answers 429 above 60. */
export const MAX_EVENTS_PER_MINUTE = 40;

const WINDOW_MS = 60_000;
/** Same list as `USAGE_EVENT_KINDS` in `@dcs/contracts` (a test keeps them equal; no runtime zod here). */
const KINDS: readonly string[] = [
  'page_view',
  'theory_exam_started',
  'theory_exam_finished',
  'sim_exam_started',
  'sim_exam_finished',
];
const FINISHED: readonly string[] = ['theory_exam_finished', 'sim_exam_finished'];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const CLASS = /^[A-Z][A-Z0-9]{0,3}$/;

/** Posts one JSON body. Returns normally on any outcome: the tracker never retries. */
export type TrackSend = (url: string, body: string) => void;

export interface TrackTimers {
  set(callback: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

export interface TrackerOptions {
  /** Full URL (or same-origin path) of `POST /api/track`. */
  endpoint: string;
  /** Holds the anonymous id under `dcs:anon-id`. May throw: the id then lives in memory. */
  storage: KeyValueStorage;
  /** Epoch ms. */
  now: () => number;
  /** Uniform random in [0, 1). */
  random: () => number;
  send: TrackSend;
  timers: TrackTimers;
  /** `false` = do nothing (DNT / GPC). Default: enabled. */
  enabled?: () => boolean;
  /** `false` = drop (offline). Default: online. */
  online?: () => boolean;
}

export interface Tracker {
  /** Queue one event; ignored when disabled, offline, over the local cap or malformed. */
  track(event: UsageEvent): void;
  /** Send what is buffered now (page hide). */
  flush(): void;
}

/** UUID v4 from 16 random bytes. */
export function uuidV4(random: () => number): string {
  const bytes = Array.from({ length: 16 }, () => Math.floor(random() * 256) & 255);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The sendable copy of an event, or null when it is not a valid `UsageEvent`. Extra fields are dropped. */
function clean(event: UsageEvent): UsageEvent | null {
  const { kind, licenseClass, passed } = event;
  if (!KINDS.includes(kind)) return null;
  if (licenseClass !== undefined && !CLASS.test(licenseClass)) return null;
  if (passed !== undefined && (typeof passed !== 'boolean' || !FINISHED.includes(kind))) {
    return null;
  }
  return {
    kind,
    ...(licenseClass === undefined ? {} : { licenseClass }),
    ...(passed === undefined ? {} : { passed }),
  };
}

export function createTracker(options: TrackerOptions): Tracker {
  const { endpoint, storage, now, random, send, timers } = options;
  const enabled = options.enabled ?? (() => true);
  const online = options.online ?? (() => true);

  let anonId: string | null = null;
  let buffer: UsageEvent[] = [];
  let timer: unknown = null;
  let windowStart = 0;
  let windowCount = 0;

  function id(): string {
    if (anonId !== null) return anonId;
    try {
      const stored = storage.get(ANON_ID_KEY);
      if (stored !== null && UUID.test(stored)) anonId = stored;
    } catch {
      // Blocked storage: fall through to a fresh in-memory id.
    }
    if (anonId === null) {
      anonId = uuidV4(random);
      try {
        storage.set(ANON_ID_KEY, anonId);
      } catch {
        // Not persisted: a new id next visit, which only makes the count of devices coarser.
      }
    }
    return anonId;
  }

  function stopTimer(): void {
    if (timer !== null) timers.clear(timer);
    timer = null;
  }

  function flush(): void {
    stopTimer();
    const events = buffer;
    buffer = [];
    if (events.length === 0 || !online() || !enabled()) return;
    try {
      send(endpoint, JSON.stringify({ anonId: id(), events }));
    } catch {
      // Analytics must never break the page.
    }
  }

  return {
    track(event) {
      if (!enabled() || !online()) return;
      const safe = clean(event);
      if (safe === null) return;
      const t = now();
      if (t - windowStart >= WINDOW_MS || t < windowStart) {
        windowStart = t;
        windowCount = 0;
      }
      if (windowCount >= MAX_EVENTS_PER_MINUTE) return;
      windowCount += 1;
      buffer.push(safe);
      if (buffer.length >= FLUSH_SIZE) {
        flush();
        return;
      }
      // Max-wait, not idle: started by the first buffered event of a batch and never reset.
      if (timer === null) timer = timers.set(flush, FLUSH_MAX_WAIT_MS);
    },
    flush,
  };
}
